import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, ArrowRight, IndianRupee, FileText, CheckCircle2, AlertCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";

/**
 * Public "Track my booking" page — no login required.
 * User provides their booking ID + the email they used at booking; we look up
 * the record via GET /api/bookings/lookup and show a compact summary.
 */
export default function Dashboard() {
  const [form, setForm] = useState({ id: "", email: "" });
  const [booking, setBooking] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBooking(null); setBusy(true);
    try {
      const { data } = await api.get("/bookings/lookup", {
        params: { id: form.id.trim(), email: form.email.trim() },
      });
      setBooking(data);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-14 w-full">
        <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Track your booking</div>
        <h1 className="font-display text-3xl sm:text-4xl text-black mt-1 mb-3 font-bold">Look up your ad.</h1>
        <p className="text-sm text-gray-600 mb-8">Enter the <strong>Booking ID</strong> you received on the confirmation page (and by email) along with the <strong>email address</strong> you used at checkout.</p>

        <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end bg-white border border-black/15 rounded-sm p-5" data-testid="lookup-form">
          <div>
            <label className="block text-[10px] tracking-overline uppercase font-bold text-slate-600 mb-1.5">Booking ID</label>
            <input required value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 focus:border-black outline-none rounded-sm bg-white font-mono text-sm"
              placeholder="a5e560d2-58b0-…" data-testid="lookup-id"/>
          </div>
          <div>
            <label className="block text-[10px] tracking-overline uppercase font-bold text-slate-600 mb-1.5">Email at booking</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 focus:border-black outline-none rounded-sm bg-white text-sm"
              placeholder="you@example.com" data-testid="lookup-email"/>
          </div>
          <button type="submit" disabled={busy}
            className="bg-black text-white px-5 py-3 rounded-sm font-semibold flex items-center justify-center gap-2 hover:bg-gray-800 disabled:opacity-60"
            data-testid="lookup-submit">
            {busy ? <Loader2 size={16} className="animate-spin"/> : <Search size={16}/>}
            {busy ? "Looking…" : "Track"}
          </button>
        </form>

        {err && (
          <div className="mt-5 px-4 py-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm flex items-start gap-2" data-testid="lookup-error">
            <AlertCircle size={16} className="mt-0.5 shrink-0"/> <span>{err}</span>
          </div>
        )}

        {booking && (
          <div className="mt-8 bg-white border border-black/15 rounded-sm p-6 space-y-5" data-testid="lookup-result">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">{booking.ad_type === "change_of_name" ? "Change of Name" : "Public Notice"}</div>
                <div className="font-display text-2xl text-black font-bold mt-1">{booking.paper_name}</div>
                <div className="text-sm text-gray-500 mt-0.5">To be published on <strong className="text-black">{booking.publish_date}</strong> · {booking.language}</div>
              </div>
              <div className={"px-3 py-1.5 text-xs uppercase tracking-overline font-bold rounded-sm " + (
                booking.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" :
                booking.payment_status === "paid_mock" ? "bg-emerald-100 text-emerald-800" :
                "bg-yellow-100 text-yellow-800"
              )}>
                {booking.payment_status === "paid" || booking.payment_status === "paid_mock" ? <span className="inline-flex items-center gap-1"><CheckCircle2 size={12}/> Paid</span> : booking.payment_status || "Pending"}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 text-sm font-mono border-t border-black/10 pt-4">
              <div className="flex justify-between"><span className="text-gray-500">Booking ID</span><span className="text-black text-xs break-all">{booking.id}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Ad size</span><span>{booking.ad_size}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Base</span><span>₹{Number(booking.base_amount || 0).toFixed(2)}</span></div>
              <div className="flex justify-between text-emerald-700"><span>Discount</span><span>– ₹{Number(booking.discount_amount || 0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">GST</span><span>₹{Number(booking.gst_amount || 0).toFixed(2)}</span></div>
              <div className="flex justify-between font-bold text-black text-base"><span>Total</span><span><IndianRupee size={12} className="inline"/>{Number(booking.total_amount || 0).toFixed(2)}</span></div>
            </div>

            <div className="border-t border-black/10 pt-4 text-sm text-gray-600 flex items-start gap-2">
              <FileText size={14} className="mt-0.5 shrink-0 text-black"/>
              <span>A PDF invoice + ad artwork was emailed to <strong className="text-black">{booking.guest_email || (booking.advocate || {}).email || "the address on file"}</strong> on confirmation. Please check your inbox / spam.</span>
            </div>
          </div>
        )}

        <div className="mt-10 text-sm text-gray-500 border-t border-black/10 pt-6">
          Need to book a new ad? <Link to="/book" className="text-black font-semibold hover:underline inline-flex items-center gap-1">Start here <ArrowRight size={14}/></Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
